import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import PurchaseDetailDialog from "@/components/PurchaseDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, ShoppingCart, MoreVertical, Pencil, Trash2, Trash, Loader2, Wallet, Boxes } from "lucide-react";import { toast } from "sonner";

const emptyItem = () => ({ description: "", quantite: 1, prix_unitaire: 0 });

export default function Purchases() {
  const [items, setItems] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = () => api.get("/purchases").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/suppliers").then((r) => setSuppliers(r.data)); api.get("/warehouses").then((r) => setWarehouses(r.data)); api.get("/articles").then((r) => setArticles(r.data)); }, []);

  const openNew = () => { setForm({ supplier_id: "", date: new Date().toISOString().slice(0, 10), echeance: "", magasin_id: "", auto_stock: false, notes: "", items: [emptyItem()], fees: [] }); setOpen(true); };
  const openReappro = async () => {
    try {
      const r = await api.get("/stock/reappro");
      if (!r.data.count) { toast.info("Aucun article sous le stock minimum"); return; }
      const its = r.data.items.map((it) => ({ description: it.description, article_id: it.article_id, quantite: it.quantite, prix_unitaire: it.prix_unitaire }));
      setForm({ supplier_id: "", date: new Date().toISOString().slice(0, 10), echeance: "", magasin_id: "", auto_stock: false, notes: "Bon d'achat de réapprovisionnement automatique", items: its, fees: [] });
      setOpen(true);
      toast.success(`${r.data.count} article(s) sous le seuil pré-rempli(s)`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openEdit = (p) => { setForm({ ...p, auto_stock: false, items: p.items?.length ? p.items : [emptyItem()], fees: p.fees || [] }); setOpen(true); };
  const setItem = (i, k, v) => setForm((p) => { const its = [...p.items]; its[i] = { ...its[i], [k]: (k === "description" || k === "lot_reference") ? v : Number(v) }; return { ...p, items: its }; });
  const pickArticle = (i, aid) => setForm((p) => { const a = articles.find((x) => x.id === aid); const its = [...p.items]; its[i] = { ...its[i], article_id: aid, description: a?.designation || its[i].description, prix_unitaire: a?.prix_achat || its[i].prix_unitaire }; return { ...p, items: its }; });
  const addItem = () => setForm((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  const rmItem = (i) => setForm((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }));
  const addFee = () => setForm((p) => ({ ...p, fees: [...(p.fees || []), { type: "Transport", montant: 0, affectation: "global", article_id: "" }] }));
  const setFee = (i, k, v) => setForm((p) => { const fs = [...(p.fees || [])]; fs[i] = { ...fs[i], [k]: k === "montant" ? Number(v) : v }; return { ...p, fees: fs }; });
  const rmFee = (i) => setForm((p) => ({ ...p, fees: p.fees.filter((_, j) => j !== i) }));
  const total = (form?.items || []).reduce((s, it) => s + (it.quantite || 0) * (it.prix_unitaire || 0), 0);
  const totalFrais = (form?.fees || []).reduce((s, f) => s + (Number(f.montant) || 0), 0);

  const save = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) { toast.error("Fournisseur requis"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/purchases/${form.id}`, form);
      else await api.post("/purchases", form);
      toast.success("Achat enregistré"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet achat ?")) return; await api.delete(`/purchases/${id}`); toast.success("Supprimé"); load(); };

  const list = items || [];
  const detteTotal = list.reduce((s, p) => s + (p.solde || 0), 0);

  return (
    <div>
      <PageHeader title="Achats fournisseurs" subtitle="Factures fournisseurs, paiements & dettes">
        <div className="rounded-lg border bg-white px-4 py-2 mr-2 hidden sm:block">
          <p className="text-[10px] uppercase font-mono text-slate-400">Dette fournisseurs</p>
          <p className="font-mono font-bold text-rose-600">{fmtMoney(detteTotal)}</p>
        </div>
        <Button variant="outline" onClick={openReappro} data-testid="reappro-btn"><Boxes className="h-4 w-4 mr-1.5" />Réappro auto</Button>
        <Button onClick={openNew} data-testid="add-purchase" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel achat</Button>
      </PageHeader>

      {items === null ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="Aucun achat" subtitle="Enregistrez une facture fournisseur." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>N°</TableHead><TableHead>Fournisseur</TableHead><TableHead>Date</TableHead><TableHead>Échéance</TableHead>
              <TableHead>Total</TableHead><TableHead>Payé</TableHead><TableHead>Dette</TableHead><TableHead>État</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`purchase-row-${p.id}`} onClick={() => setDetailId(p.id)}>
                  <TableCell className="font-mono text-xs text-slate-500">{p.numero}</TableCell>
                  <TableCell className="font-semibold">{p.supplier_name}</TableCell>
                  <TableCell className="text-sm">{fmtDate(p.date)}</TableCell>
                  <TableCell className="text-sm">{p.echeance ? fmtDate(p.echeance) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs font-bold">{fmtMoney(p.total)}</TableCell>
                  <TableCell className="font-mono text-xs text-emerald-600">{fmtMoney(p.montant_paye)}</TableCell>
                  <TableCell className={`font-mono text-xs font-semibold ${p.solde > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtMoney(p.solde)}</TableCell>
                  <TableCell><StatusBadge value={p.dette_etat} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`purchase-menu-${p.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetailId(p.id)} data-testid={`purchase-pay-${p.id}`}><Wallet className="h-4 w-4 mr-2" />Paiements</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(p)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(p.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier l'achat" : "Nouvel achat fournisseur"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Fournisseur</Label>
                  <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="purchase-supplier"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Échéance</Label><Input type="date" value={form.echeance || ""} onChange={(e) => setForm({ ...form, echeance: e.target.value })} data-testid="purchase-echeance" className="mt-1" /></div>
              </div>
              {!form.id && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg border bg-slate-50 p-3">
                  <div><Label className="text-xs">Magasin (entrée de stock)</Label>
                    <Select value={form.magasin_id || ""} onValueChange={(v) => setForm({ ...form, magasin_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="purchase-magasin"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                      <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.nom}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={!!form.auto_stock} onChange={(e) => setForm({ ...form, auto_stock: e.target.checked })} data-testid="purchase-autostock" className="h-4 w-4 accent-green-700" />
                    Générer l'entrée de stock (lignes liées à un article)
                  </label>
                </div>
              )}
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500"><tr>
                    <th className="text-left p-2 min-w-40">Article (option)</th><th className="text-left p-2 min-w-40">Désignation</th><th className="p-2">Qté</th><th className="p-2">P.U.</th><th className="p-2">Total</th><th></th>
                  </tr></thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1">
                          <Select value={it.article_id || ""} onValueChange={(v) => pickArticle(i, v)}>
                            <SelectTrigger className="h-8" data-testid={`pitem-article-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.designation}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="p-1"><Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`pitem-desc-${i}`} className="h-8" /></td>
                        <td className="p-1"><Input type="number" value={it.quantite} onChange={(e) => setItem(i, "quantite", e.target.value)} data-testid={`pitem-qty-${i}`} className="h-8 w-20" /></td>
                        <td className="p-1"><Input type="number" value={it.prix_unitaire} onChange={(e) => setItem(i, "prix_unitaire", e.target.value)} data-testid={`pitem-pu-${i}`} className="h-8 w-28" /></td>
                        <td className="p-1 font-mono text-xs text-right whitespace-nowrap">{fmtMoney((it.quantite || 0) * (it.prix_unitaire || 0))}</td>
                        <td className="p-1"><Button type="button" variant="ghost" size="icon" onClick={() => rmItem(i)}><Trash className="h-4 w-4 text-rose-500" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-2 border-t bg-slate-50 flex items-center justify-between">
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-pitem"><Plus className="h-4 w-4 mr-1" />Ajouter une ligne</Button>
                  <span className="font-mono font-bold text-brand-600">Articles : {fmtMoney(total)}</span>
                </div>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b"><span className="text-sm font-semibold">Frais sur achat</span><Button type="button" variant="outline" size="sm" onClick={addFee} data-testid="add-fee"><Plus className="h-4 w-4 mr-1" />Ajouter un frais</Button></div>
                {(form.fees || []).length === 0 ? <p className="px-3 py-2 text-xs text-slate-400">Aucun frais (transport, douane, manutention, assurance…).</p> : (
                  <table className="w-full text-sm"><tbody>
                    {form.fees.map((f, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1"><Input value={f.type} onChange={(e) => setFee(i, "type", e.target.value)} data-testid={`fee-type-${i}`} placeholder="Type" className="h-8" /></td>
                        <td className="p-1"><Input type="number" value={f.montant} onChange={(e) => setFee(i, "montant", e.target.value)} data-testid={`fee-montant-${i}`} placeholder="Montant" className="h-8 w-32" /></td>
                        <td className="p-1">
                          <Select value={f.affectation} onValueChange={(v) => setFee(i, "affectation", v)}><SelectTrigger className="h-8 w-40" data-testid={`fee-affectation-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="global">Global</SelectItem><SelectItem value="article">Affecté à un article</SelectItem></SelectContent></Select>
                        </td>
                        <td className="p-1">{f.affectation === "article" && (
                          <Select value={f.article_id || ""} onValueChange={(v) => setFee(i, "article_id", v)}><SelectTrigger className="h-8 w-40" data-testid={`fee-article-${i}`}><SelectValue placeholder="Article" /></SelectTrigger>
                            <SelectContent>{form.items.filter((it) => it.article_id).map((it) => <SelectItem key={it.article_id} value={it.article_id}>{it.description}</SelectItem>)}</SelectContent></Select>)}</td>
                        <td className="p-1"><Button type="button" variant="ghost" size="icon" onClick={() => rmFee(i)}><Trash className="h-4 w-4 text-rose-500" /></Button></td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
                <div className="p-2 border-t bg-slate-50 flex items-center justify-end gap-4 text-sm">
                  <span className="text-slate-500">Frais : <span className="font-mono">{fmtMoney(totalFrais)}</span></span>
                  <span className="font-mono font-bold text-brand-600" data-testid="purchase-cout-total">Coût total : {fmtMoney(total + totalFrais)}</span>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="purchase-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer l'achat"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <PurchaseDetailDialog purchaseId={detailId} onOpenChange={(o) => !o && setDetailId(null)} onChanged={load} />
    </div>
  );
}
